import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { ComponentRenderer } from '../cycles/cycle-execution/ComponentRenderer'

/**
 * @fileoverview Task Access Page (Public)
 *
 * Public page for users to access tasks via magic link without logging in
 * Accessed via: /tasks/access/{token}
 */

type FormValues = Record<string, string | number | boolean>

export const TaskAccessPage = () => {
	const { token } = useParams<{ token: string }>()
	const { showToast } = useToast()
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [isSaving, setIsSaving] = useState(false)

	// Validate token
	const { data: validation, isLoading: isValidating } = useQuery({
		...trpc.public.tasks.validateToken.queryOptions({ token: token as string }),
		enabled: !!token,
	})

	// Get task details by token
	const { data: taskData, isLoading: isLoadingTask } = useQuery({
		...trpc.public.tasks.getByToken.queryOptions({ token: token as string }),
		enabled: !!token && validation?.valid,
	})

	// Save form mutation (auto-save)
	const saveFormMutation = useMutation({
		...trpc.public.tasks.saveFormByToken.mutationOptions(),
		onSuccess: () => {
			setIsSaving(false)
		},
		onError: (error) => {
			console.error('Error saving form:', error)
			setIsSaving(false)
		},
	})

	// Submit form mutation
	const submitFormMutation = useMutation({
		...trpc.public.tasks.submitFormByToken.mutationOptions(),
		onSuccess: () => {
			setIsSubmitted(true)
			showToast('Task submitted successfully!')
		},
		onError: (error) => {
			const message = error instanceof Error ? error.message : 'Failed to submit task. Please try again.'
			showToast(message)
		},
	})

	const form = useForm<FormValues>({
		defaultValues: {},
	})

	const formatHeaderFieldValue = (field: {
		value: string | number
		sources?: Array<{ type: string; key: string }>
	}) => {
		const rawValue = field.value
		if (rawValue === undefined || rawValue === null || rawValue === '') return '-'

		const isSystemUpdatedAt = field.sources?.some((source) => source.type === 'system' && source.key === 'updatedAt')

		if (isSystemUpdatedAt) {
			const date = new Date(String(rawValue))
			if (!Number.isNaN(date.getTime())) {
				return date.toLocaleString(undefined, {
					year: 'numeric',
					month: 'short',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
				})
			}
		}

		return String(rawValue)
	}

	// Load existing responses into form
	useEffect(() => {
		if (taskData?.formComponents) {
			const defaultValues: FormValues = {}
			taskData.formComponents.forEach((component) => {
				if (component.response) {
					defaultValues[component.id] = component.response
				}
			})
			form.reset(defaultValues)
		}
	}, [taskData, form])

	// Auto-save on form changes
	useEffect(() => {
		if (!taskData) return

		const subscription = form.watch((data) => {
			setIsSaving(true)

			// Debounce save
			const timeoutId = setTimeout(() => {
				const responses = Object.entries(data)
					.filter(([_, value]) => value !== undefined && value !== '')
					.map(([componentId, response]) => ({
						componentId,
						response: String(response),
					}))

				if (responses.length > 0) {
					saveFormMutation.mutate({
						token: token as string,
						responses,
					})
				} else {
					setIsSaving(false)
				}
			}, 1000)

			return () => clearTimeout(timeoutId)
		})

		return () => subscription.unsubscribe()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskData, form.watch, saveFormMutation.mutate, token])

	const handleSubmit = form.handleSubmit(async (data) => {
		const responses = Object.entries(data)
			.filter(([_, value]) => value !== undefined && value !== '')
			.map(([componentId, response]) => ({
				componentId,
				response: String(response),
			}))

		if (responses.length === 0) {
			showToast('Please fill out at least one field before submitting', 'error')
			return
		}

		await submitFormMutation.mutateAsync({
			token: token as string,
			responses,
		})
	})

	// Loading state
	if (isValidating || isLoadingTask) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<SpinnerWithBackground />
			</div>
		)
	}

	// Invalid token
	if (!validation?.valid) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
					<div className='mb-6'>
						<div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<svg
								className='w-8 h-8 text-red-600'
								fill='none'
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth='2'
								viewBox='0 0 24 24'
								stroke='currentColor'
								aria-hidden='true'>
								<path d='M6 18L18 6M6 6l12 12'></path>
							</svg>
						</div>
						<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
							{validation?.error === 'TOKEN_EXPIRED'
								? 'Link Expired'
								: validation?.error === 'TOKEN_REVOKED'
									? 'Link Revoked'
									: validation?.error === 'TOKEN_ALREADY_USED'
										? 'Task Already Submitted'
										: 'Invalid Link'}
						</Text>
						<Text variant='small' className='text-gray-600'>
							{validation?.message || 'This link is invalid or has expired.'}
						</Text>
					</div>
					<Text variant='xsmall' className='text-gray-500'>
						Please contact your administrator for assistance.
					</Text>
				</div>
			</div>
		)
	}

	// Success state
	if (isSubmitted) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
					<div className='mb-6'>
						<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<svg
								className='w-8 h-8 text-green-600'
								fill='none'
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth='2'
								viewBox='0 0 24 24'
								stroke='currentColor'
								aria-hidden='true'>
								<path d='M5 13l4 4L19 7'></path>
							</svg>
						</div>
						<Text variant='large' weight='semibold' className='text-gray-900 mb-2'>
							Thank You!
						</Text>
						<Text variant='small' className='text-gray-600'>
							Your task has been submitted successfully.
						</Text>
					</div>
					<Text variant='xsmall' className='text-gray-500'>
						You can close this window now.
					</Text>
				</div>
			</div>
		)
	}

	// Render task
	return (
		<div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4'>
			<div className='max-w-4xl mx-auto'>
				<div className='bg-white rounded-lg shadow-xl overflow-hidden'>
					{/* Header */}
					<div className='bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6'>
						<Text variant='large' weight='semibold' className='text-white mb-2 text-2xl'>
							{taskData?.taskName || 'Task'}
						</Text>
						<Text variant='small' className='text-blue-100'>
							{taskData?.evaluation?.cycleName || 'Performance Review'}
						</Text>
					</div>

					{/* Content */}
					<div className='px-8 py-8'>
						{/* Participant Info (if available) */}
						{taskData?.participant && (
							<div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md'>
								<div className='space-y-1'>
									{taskData.evaluationHeaderConfig?.map((field) => {
										const formattedValue = formatHeaderFieldValue(field)
										return (
											<div key={field.id}>
												<Text variant='xsmall' className='text-blue-700 font-semibold'>
													{field.name}:
												</Text>
												<Text variant='small' className='text-blue-800 ml-2 break-words'>
													{formattedValue}
												</Text>
											</div>
										)
									})}
								</div>
							</div>
						)}

						{/* Form */}
						<FormProvider {...form}>
							<form onSubmit={handleSubmit}>
								<div className='space-y-6'>
									{taskData?.formComponents?.map((component) => (
										<ComponentRenderer
											key={component.id}
											component={component}
											control={form.control}
											evaluationId={taskData.evaluation.id}
											currentStepId={taskData.cycleStepId}
											userRole={taskData.calculatedRole}
											canEdit={true}
											allowCAEdit={taskData.allowCAEdit}
											aiSummaries={taskData.aiSummaries}
										/>
									))}
								</div>

								{/* Submit Button */}
								<div className='mt-8 flex justify-end gap-4'>
									{isSaving && (
										<Text variant='xsmall' className='text-gray-500 self-center'>
											Saving...
										</Text>
									)}
									<Button
										variant='primary'
										onClick={handleSubmit}
										disabled={submitFormMutation.isPending}
										className='px-8 py-3'>
										{submitFormMutation.isPending ? 'Submitting...' : 'Submit Task'}
									</Button>
								</div>
							</form>
						</FormProvider>
					</div>

					{/* Footer */}
					<div className='bg-gray-50 px-8 py-4 border-t border-gray-200'>
						<Text variant='xsmall' className='text-gray-600 text-center'>
							Your responses are automatically saved as you type
						</Text>
					</div>
				</div>
			</div>
		</div>
	)
}
